/**
 * Registry of hand-written content pages.
 *
 * Replaces the earlier block-JSON renderer: these pages are near-static
 * marketing copy, and plain Svelte markup is easier to read and change than a
 * layout inferred from "section roles".
 *
 * The registry exists because localised URLs cannot be derived — WPML
 * translates page slugs, so `/about-us` is `/th/เกี่ยวกับเรา`, not
 * `/th/about-us`. Header, footer and routing all resolve through `pagePath`.
 */

import type { Locale } from '$lib/i18n';

/**
 * Serialisable page metadata.
 *
 * The Svelte component is deliberately **not** part of this: a server `load`
 * cannot serialise a function, and returning one fails the render with
 * "Cannot stringify a function". Components are looked up separately, and
 * lazily, by `loadPageBody` in `$lib/pages/body` — which is also why they are
 * not imported here: this module is reached from the header and the footer, and
 * anything it imports lands in every page's bundle.
 */
export interface PageEntry {
	/** Locale-stable identifier used by links. */
	key: string;
	locale: Locale;
	/** Production URL for this locale. */
	path: string;
	title: string;
	description?: string;
}

export const PAGES: PageEntry[] = [
	{
		key: 'home',
		locale: 'en',
		path: '/',
		title: 'Dutch Passion Thailand — Cannabis Seeds',
		description:
			"Premium cannabis seeds from Dutch Passion, one of the world's oldest seed banks, delivered across Thailand with cash on delivery and same-day Bangkok dispatch."
	},
	{
		key: 'home',
		locale: 'th',
		path: '/th',
		title: 'ดัตช์แพสชั่น® — เมล็ดกัญชา',
		description:
			'เมล็ดพันธุ์กัญชาคุณภาพสูงจาก Dutch Passion หนึ่งในธนาคารเมล็ดพันธุ์ที่เก่าแก่ที่สุดในโลก จัดส่งทั่วประเทศไทย พร้อมบริการเก็บเงินปลายทางและจัดส่งด่วนในกรุงเทพฯ'
	},
	{
		key: 'about',
		locale: 'en',
		path: '/about-us',
		title: 'About Us',
		description:
			"Dutch Passion is one of the world's oldest cannabis seed banks, founded in Amsterdam in 1987. Read how that breeding history reaches growers in Thailand today."
	},
	{
		key: 'about',
		locale: 'th',
		path: '/th/เกี่ยวกับเรา',
		title: 'เกี่ยวกับเรา',
		description:
			'Dutch Passion เป็นหนึ่งในธนาคารเมล็ดพันธุ์กัญชาที่เก่าแก่ที่สุดในโลก ก่อตั้งที่อัมสเตอร์ดัมในปี 1987 อ่านเรื่องราวการปรับปรุงพันธุ์ที่ส่งถึงผู้ปลูกไทย'
	},
	{
		key: 'contact',
		locale: 'en',
		path: '/contact',
		title: 'Contact Us',
		description:
			'Get in touch with Dutch Passion Thailand about orders, delivery or growing advice. We reply within 24 hours on working days, in English and Thai.'
	},
	{
		key: 'contact',
		locale: 'th',
		path: '/th/ติดต่อ',
		title: 'ติดต่อเรา',
		description:
			'ติดต่อ Dutch Passion Thailand เรื่องคำสั่งซื้อ การจัดส่ง หรือคำแนะนำการปลูก เราตอบกลับภายใน 24 ชั่วโมงในวันทำการ ทั้งภาษาไทยและภาษาอังกฤษ'
	},
	/*
	 * The FAQ is the one page here that never existed on WordPress, so unlike
	 * every other entry its paths are chosen rather than taken from WPML. It was
	 * previously the `#faq` section of the contact page.
	 */
	{
		key: 'faq',
		locale: 'en',
		path: '/faq',
		title: 'Frequently Asked Questions',
		description:
			"Answers on delivery, payment, seed types, germination and growing cannabis in Thailand's climate, from the Dutch Passion Thailand team."
	},
	{
		key: 'faq',
		locale: 'th',
		path: '/th/คำถามที่พบบ่อย',
		title: 'คำถามที่พบบ่อย',
		description:
			'คำตอบเรื่องการจัดส่ง การชำระเงิน ชนิดของเมล็ดพันธุ์ การเพาะเมล็ด และการปลูกกัญชาในสภาพอากาศเมืองไทย จากทีมงาน Dutch Passion Thailand'
	},
	/*
	 * Cannabis and the law.
	 *
	 * Like the FAQ, this page never existed on WordPress, so its paths are chosen
	 * rather than inherited from WPML.
	 *
	 * **All eight locales.** This was English and Thai only for a time, on the
	 * view that machine-translated copy awaiting native review is an acceptable
	 * standard for marketing and not for a page describing regulation. The other
	 * six now carry it too, with the same translation notice the rest of their
	 * copy has: the page names and dates every instrument, states the law rather
	 * than advising on it, and closes by telling the reader to confirm the
	 * current position with the Thai FDA or DTAM rather than rely on a
	 * commercial site. A reader in one of those five previously got no page at
	 * all, which was not the safer outcome it looked like.
	 */
	{
		key: 'cannabis-law',
		locale: 'en',
		path: '/cannabis-law-thailand',
		/*
		 * Kept short on purpose. `Seo.svelte` appends ` | Dutch Passion Thailand`,
		 * 24 characters, and the previous wording — "Cannabis Seeds and the Law in
		 * Thailand" — took the rendered title to 63, past the ~60 where Google
		 * truncates on desktop. This says the same thing in 29 and leaves the
		 * brand suffix intact, which is the half that was being cut.
		 */
		title: 'Cannabis Seed Law in Thailand',
		description:
			'What Thai law says about buying cannabis seeds, growing them and the GACP paperwork a licensed grower needs — regulations named and dated, updated for 2026.'
	},
	{
		key: 'cannabis-law',
		locale: 'th',
		path: '/th/กฎหมายกัญชา',
		title: 'เมล็ดพันธุ์กัญชากับกฎหมายไทย',
		description:
			'กฎหมายไทยว่าอย่างไรเรื่องการซื้อเมล็ดพันธุ์กัญชา การปลูก และเอกสาร GACP ที่ผู้ปลูกซึ่งได้รับอนุญาตต้องใช้ พร้อมระบุชื่อและวันที่ของกฎหมายแต่ละฉบับ'
	},
	/*
	 * The six added locales. Their slugs are chosen, like the English and Thai
	 * ones — WPML never published this page in any language.
	 */
	{
		key: 'cannabis-law',
		locale: 'ja',
		path: '/ja/タイの大麻法',
		title: 'タイにおけるカンナビスシードと法律',
		description:
			'タイ法におけるカンナビスシードの位置づけ、購入できる人、栽培の届出、そして認可栽培者に必要なGACP書類。法令名と日付を明示し、2026年時点に更新。'
	},
	{
		key: 'cannabis-law',
		locale: 'fr',
		path: '/fr/cannabis-et-loi-thailande',
		title: 'Graines de cannabis et loi en Thaïlande',
		description:
			'Ce que le droit thaïlandais dit de l’achat de graines de cannabis, de leur culture et des documents GACP exigés d’un cultivateur sous licence, textes datés.'
	},
	{
		key: 'cannabis-law',
		locale: 'es',
		path: '/es/cannabis-y-ley-tailandia',
		title: 'Semillas de cannabis y la ley en Tailandia',
		description:
			'Qué dice la ley tailandesa sobre comprar semillas de cannabis, cultivarlas y la documentación GACP que necesita un cultivador con licencia, con normas fechadas.'
	},
	{
		key: 'cannabis-law',
		locale: 'ru',
		path: '/ru/закон-о-каннабисе',
		title: 'Семена каннабиса и закон в Таиланде',
		description:
			'Что тайский закон говорит о покупке семян каннабиса, их выращивании и документах GACP для лицензированного производителя — с названиями и датами актов.'
	},
	{
		key: 'cannabis-law',
		locale: 'zh',
		path: '/zh/泰国大麻法律',
		title: '泰国的大麻种子与法律',
		description:
			'泰国法律关于购买大麻种子、种植以及持证种植者所需 GACP 文件的规定，法规均写明名称与日期，内容更新至 2026 年。'
	},
	{
		key: 'cannabis-law',
		locale: 'vi',
		path: '/vi/luat-can-sa-thai-lan',
		title: 'Hạt giống cần sa và luật pháp Thái Lan',
		description:
			'Luật Thái Lan nói gì về việc mua hạt giống cần sa, trồng cây và hồ sơ GACP mà người trồng có giấy phép phải có, kèm tên và ngày của từng văn bản.'
	},
	{
		key: 'resellers',
		locale: 'en',
		path: '/resellers',
		title: 'Resellers',
		description:
			'Dispensaries and shops across Bangkok, Phuket and the rest of Thailand where you can buy Dutch Passion cannabis seeds in person, with addresses and maps.'
	},
	{
		key: 'resellers',
		locale: 'th',
		path: '/th/ตัวแทนจำหน่าย',
		title: 'ตัวแทนจำหน่าย',
		description:
			'ร้านขายยาและร้านค้าในกรุงเทพฯ ภูเก็ต และทั่วประเทศไทย ที่คุณสามารถซื้อเมล็ดพันธุ์กัญชา Dutch Passion ได้ด้วยตนเอง พร้อมที่อยู่และแผนที่'
	},
	/*
	 * The four revived locales.
	 *
	 * `about` and `contact` keep the slugs WPML published — `/ja/会社概要`, not
	 * `/ja/about-us` — because those are the URLs search engines indexed and the
	 * only ones that can carry any remaining authority. They are recorded in
	 * `scripts/lib/legacy-urls.mjs`, which doubles as the restoration map.
	 *
	 * `faq` and `resellers` never existed in these languages, so their slugs are
	 * chosen rather than inherited.
	 *
	 * All copy here is machine-translated and awaits native review.
	 */
	{
		key: 'home',
		locale: 'ja',
		path: '/ja',
		title: 'ダッチパッション — カンナビスシード',
		description:
			'世界最古級のシードバンク Dutch Passion のプレミアムなカンナビスシードを、タイ全国へ代金引換・当日発送でお届けします。'
	},
	{
		key: 'about',
		locale: 'ja',
		path: '/ja/会社概要',
		title: '会社概要',
		description:
			'Dutch Passion は1987年にアムステルダムで創業した世界最古級のカンナビスシードバンクです。その育種の歴史が今日タイの栽培者へどう届くのかをご紹介します。'
	},
	{
		key: 'contact',
		locale: 'ja',
		path: '/ja/連絡先',
		title: 'お問い合わせ',
		description:
			'ご注文、配送、栽培のご相談は Dutch Passion Thailand へ。営業日は24時間以内に、タイ語と英語で返信します。'
	},
	{
		key: 'faq',
		locale: 'ja',
		path: '/ja/よくある質問',
		title: 'よくあるご質問',
		description:
			'配送、支払い、シードの種類、発芽、そしてタイの気候での栽培について、Dutch Passion Thailand チームがお答えします。'
	},
	{
		key: 'resellers',
		locale: 'ja',
		path: '/ja/販売店',
		title: '販売店',
		description:
			'バンコク、プーケットをはじめタイ各地で Dutch Passion のカンナビスシードを店頭でお求めいただけるお店を、住所と地図つきでご案内します。'
	},
	{
		key: 'home',
		locale: 'fr',
		path: '/fr',
		title: 'Dutch Passion Thailand — Graines de cannabis',
		description:
			'Graines de cannabis premium de Dutch Passion, l’une des plus anciennes banques de graines au monde, livrées partout en Thaïlande avec paiement à la livraison.'
	},
	{
		key: 'about',
		locale: 'fr',
		path: '/fr/a-propos-de-nous',
		title: 'À propos',
		description:
			'Dutch Passion, fondée à Amsterdam en 1987, est l’une des plus anciennes banques de graines au monde. Son héritage arrive aujourd’hui en Thaïlande.'
	},
	{
		key: 'contact',
		locale: 'fr',
		path: '/fr/contact',
		title: 'Nous contacter',
		description:
			'Contactez Dutch Passion Thailand pour vos commandes, la livraison ou des conseils de culture. Réponse sous 24 heures les jours ouvrés.'
	},
	{
		key: 'faq',
		locale: 'fr',
		path: '/fr/faq',
		title: 'Questions fréquentes',
		description:
			'Réponses sur la livraison, le paiement, les types de graines, la germination et la culture du cannabis sous le climat thaïlandais, par Dutch Passion Thailand.'
	},
	{
		key: 'resellers',
		locale: 'fr',
		path: '/fr/revendeurs',
		title: 'Revendeurs',
		description:
			'Dispensaires et boutiques à Bangkok, Phuket et dans toute la Thaïlande où acheter les graines Dutch Passion en personne, avec adresses et plans.'
	},
	{
		key: 'home',
		locale: 'es',
		path: '/es',
		title: 'Dutch Passion Thailand — Semillas de cannabis',
		description:
			'Semillas de cannabis premium de Dutch Passion, uno de los bancos de semillas más antiguos del mundo, con entrega en toda Tailandia y pago contra reembolso.'
	},
	{
		key: 'about',
		locale: 'es',
		path: '/es/sobre-nosotros',
		title: 'Sobre nosotros',
		description:
			'Dutch Passion, fundada en Ámsterdam en 1987, es uno de los bancos de semillas más antiguos del mundo. Su historia llega hoy a Tailandia.'
	},
	{
		key: 'contact',
		locale: 'es',
		path: '/es/ponte-en-contacto-con',
		title: 'Contacto',
		description:
			'Ponte en contacto con Dutch Passion Thailand para pedidos, entregas o consejos de cultivo. Respondemos en 24 horas en días laborables, en inglés y tailandés.'
	},
	{
		key: 'faq',
		locale: 'es',
		path: '/es/preguntas-frecuentes',
		title: 'Preguntas frecuentes',
		description:
			'Respuestas sobre entrega, pago, tipos de semilla, germinación y cultivo de cannabis en el clima de Tailandia, del equipo de Dutch Passion Thailand.'
	},
	{
		key: 'resellers',
		locale: 'es',
		path: '/es/distribuidores',
		title: 'Distribuidores',
		description:
			'Dispensarios y tiendas de Bangkok, Phuket y el resto de Tailandia donde comprar semillas Dutch Passion en persona, con direcciones y mapas.'
	},
	{
		key: 'home',
		locale: 'ru',
		path: '/ru',
		title: 'Dutch Passion Thailand — семена конопли',
		description:
			'Премиальные семена конопли Dutch Passion, одного из старейших сидбанков мира, с доставкой по всему Таиланду и оплатой при получении.'
	},
	{
		key: 'about',
		locale: 'ru',
		path: '/ru/о-нас',
		title: 'О нас',
		description:
			'Dutch Passion, основанная в Амстердаме в 1987 году, — один из старейших сидбанков мира. Эта селекционная история дошла до Таиланда.'
	},
	{
		key: 'contact',
		locale: 'ru',
		path: '/ru/свяжитесь-с',
		title: 'Связаться с нами',
		description:
			'Свяжитесь с Dutch Passion Thailand по вопросам заказов, доставки или выращивания. Мы отвечаем в течение 24 часов в рабочие дни, на английском и тайском.'
	},
	{
		key: 'faq',
		locale: 'ru',
		path: '/ru/часто-задаваемые-вопросы',
		title: 'Часто задаваемые вопросы',
		description:
			'Ответы о доставке, оплате, типах семян, проращивании и выращивании конопли в климате Таиланда от команды Dutch Passion Thailand.'
	},
	{
		key: 'resellers',
		locale: 'ru',
		path: '/ru/магазины',
		title: 'Магазины',
		description:
			'Диспансарии и магазины в Бангкоке, Пхукете и по всему Таиланду, где можно купить семена Dutch Passion лично, с адресами и картами.'
	},
	/*
	 * Simplified Chinese.
	 *
	 * The seventh locale, and the only one WPML never published — so unlike
	 * ja/fr/es/ru there are no legacy slugs to restore and no redirects pointing
	 * at these URLs. The slugs below are therefore chosen, not inherited.
	 *
	 * All copy here is machine-translated and awaits native review.
	 */
	{
		key: 'home',
		locale: 'zh',
		path: '/zh',
		title: 'Dutch Passion — 大麻种子',
		description:
			'来自世界最悠久种子银行之一的 Dutch Passion 优质大麻种子，泰国全境货到付款、当日发货。'
	},
	{
		key: 'about',
		locale: 'zh',
		path: '/zh/关于我们',
		title: '关于我们',
		description:
			'Dutch Passion 是 1987 年创立于阿姆斯特丹的世界最悠久大麻种子银行之一。了解这段育种历史如何服务今天的泰国种植者。'
	},
	{
		key: 'contact',
		locale: 'zh',
		path: '/zh/联系我们',
		title: '联系我们',
		description:
			'订单、配送或种植方面的问题，欢迎联系 Dutch Passion Thailand。工作日 24 小时内以泰语和英语回复。'
	},
	{
		key: 'faq',
		locale: 'zh',
		path: '/zh/常见问题',
		title: '常见问题',
		description:
			'关于配送、付款、种子类型、发芽以及在泰国气候下种植的常见问题，由 Dutch Passion Thailand 团队解答。'
	},
	{
		key: 'resellers',
		locale: 'zh',
		path: '/zh/经销商',
		title: '经销商',
		description: '在曼谷、普吉及泰国各地可现场选购 Dutch Passion 大麻种子的门店，附地址与地图。'
	},
	/*
	 * The eighth locale.
	 *
	 * Like `zh`, WPML never published Vietnamese, so there are no legacy slugs to
	 * restore and no redirects pointing at these URLs — the slugs below are
	 * chosen, not inherited.
	 *
	 * They are also **ASCII-folded**: `gioi-thieu-ve-chung-toi`, not
	 * `giới-thiệu-về-chúng-tôi`. Vietnamese is written in Latin script, so unlike
	 * Thai, Japanese, Russian and Chinese it has a choice here, and a diacritic
	 * slug would be percent-encoded in every `href`, sitemap entry and hreflang
	 * tag for no gain. Folding is what Vietnamese sites do.
	 *
	 * All copy here is machine-translated and awaits native review.
	 */
	{
		key: 'home',
		locale: 'vi',
		path: '/vi',
		title: 'Dutch Passion — Hạt giống cần sa',
		description:
			'Hạt giống cần sa cao cấp từ Dutch Passion, một trong những ngân hàng hạt giống lâu đời nhất thế giới, giao khắp Thái Lan, thanh toán khi nhận hàng.'
	},
	{
		key: 'about',
		locale: 'vi',
		path: '/vi/gioi-thieu-ve-chung-toi',
		title: 'Về chúng tôi',
		description:
			'Dutch Passion là ngân hàng hạt giống cần sa lâu đời bậc nhất thế giới, lập tại Amsterdam năm 1987. Lịch sử lai tạo ấy đến với người trồng Thái Lan hôm nay.'
	},
	{
		key: 'contact',
		locale: 'vi',
		path: '/vi/lien-he',
		title: 'Liên hệ',
		description:
			'Liên hệ Dutch Passion Thailand về đơn hàng, giao nhận hay tư vấn trồng trọt. Chúng tôi trả lời trong 24 giờ vào ngày làm việc, bằng tiếng Anh và tiếng Thái.'
	},
	{
		key: 'faq',
		locale: 'vi',
		path: '/vi/cau-hoi-thuong-gap',
		title: 'Câu hỏi thường gặp',
		description:
			'Giải đáp về giao hàng, thanh toán, các loại hạt giống, cách nảy mầm và trồng trong khí hậu Thái Lan, từ đội ngũ Dutch Passion Thailand.'
	},
	{
		key: 'resellers',
		locale: 'vi',
		path: '/vi/dai-ly',
		title: 'Đại lý',
		description:
			'Các tiệm và cửa hàng ở Bangkok, Phuket và khắp Thái Lan nơi bạn mua trực tiếp hạt giống cần sa Dutch Passion, kèm địa chỉ và bản đồ.'
	}
];

/** Path of a known page in a locale, or undefined when it has no translation. */
export function pagePath(key: string, locale: string): string | undefined {
	return PAGES.find((page) => page.key === key && page.locale === locale)?.path;
}

export function pageByPath(path: string): PageEntry | undefined {
	const wanted = path.replace(/\/$/, '') || '/';
	return PAGES.find((page) => page.path === wanted);
}
